import React, {
  useEffect, useLayoutEffect, useMemo, useRef, useState,
} from 'react'
import {
  AxisTickStrategies, ColorHEX, SolidFill, SolidLine, translatePoint, transparentLine,
} from '@arction/lcjs'
import { ArctionDashboard, arctionHelper } from '../../../ArctionDashboard'
import { chartHelper } from '../../../helpers'
import { useAppSettings } from '../../../Contexts/AppSettingsProvider'
import {
  colorForUnitIndex,
  formatChartAxisDateTime,
  formatChartCursorTime,
  formatChartValue,
  toOriginOffsetPoints,
} from './fleetChartHelpers'

const CHART_HEIGHT = 420

const buildStroke = (colorHex) => new SolidLine({
  thickness: 1.5,
  fillStyle: new SolidFill({ color: ColorHEX(colorHex) }),
})

const valueAtTimestamp = (points, absoluteMs, toleranceMs) => {
  if (!points?.length || absoluteMs == null) return null
  let best = null
  let bestDist = Infinity
  for (let i = 0; i < points.length; i += 1) {
    const point = points[i]
    if (!point || !Number.isFinite(point.x) || !Number.isFinite(point.y)) continue
    const dist = Math.abs(point.x - absoluteMs)
    if (dist < bestDist) {
      bestDist = dist
      best = point.y
    }
  }
  if (best == null || bestDist > toleranceMs) return null
  return best
}

function getDateSpanTolerance(seriesList) {
  let minX = null
  let maxX = null
  seriesList.forEach((item) => {
    item.points?.forEach((p) => {
      if (!Number.isFinite(p.x)) return
      if (minX == null || p.x < minX) minX = p.x
      if (maxX == null || p.x > maxX) maxX = p.x
    })
  })
  if (minX == null || maxX == null) return 2 * 60 * 1000
  return Math.max(2 * 60 * 1000, (maxX - minX) * 0.02)
}

/**
 * Multi-series fleet chart (one line per unit) with a right-side cursor legend.
 * Zoom / pan / wheel / double-click reset follow the same pattern as Jobs TimeBasedChart.
 */
const FleetChart = ({
  seriesList = [],
  dateOriginMs,
  title = '',
  channelUnit = '',
  hiddenSeriesIds = new Set(),
  onToggleSeriesVisibility,
}) => {
  const { appTheme: theme } = useAppSettings()
  const [chartId] = useState(() => `fleet-chart-${Math.random().toString(36).slice(2)}`)
  const containerRef = useRef(null)
  const chartRef = useRef(null)
  const dashboardRef = useRef(null)
  const arctionRef = useRef(null)
  const seriesRefsRef = useRef([])
  const yAxisRef = useRef(null)
  const originRef = useRef(dateOriginMs)
  const seriesListRef = useRef(seriesList)
  const autoFitDisabledRef = useRef(false)

  const [cursorMs, setCursorMs] = useState(null)
  const [cursorValues, setCursorValues] = useState({})
  const [legendCollapsed, setLegendCollapsed] = useState(false)
  const [autoFitDisabled, setAutoFitDisabled] = useState(false)
  const cursorTouchedRef = useRef(false)
  const hiddenSeriesIdsRef = useRef(hiddenSeriesIds)

  const hasData = seriesList.some((s) => s.points?.length > 0)
  const seriesKey = useMemo(
    () => seriesList.map((s) => s.unitUId).join('|'),
    [seriesList],
  )

  useEffect(() => {
    originRef.current = dateOriginMs
  }, [dateOriginMs])

  useEffect(() => {
    seriesListRef.current = seriesList
  }, [seriesList])

  useEffect(() => {
    autoFitDisabledRef.current = autoFitDisabled
  }, [autoFitDisabled])

  useEffect(() => {
    hiddenSeriesIdsRef.current = hiddenSeriesIds
    applySeriesVisibility(seriesRefsRef.current, seriesListRef.current)
  }, [hiddenSeriesIds])

  // New channel / range / series set → restore autofit like Jobs on settings change
  useEffect(() => {
    cursorTouchedRef.current = false
    setCursorMs(null)
    setCursorValues({})
    setAutoFitDisabled(false)
    autoFitDisabledRef.current = false
  }, [seriesKey, dateOriginMs, title, channelUnit])

  const applySeriesVisibility = (lineSeriesArr = seriesRefsRef.current, list = seriesList) => {
    const hidden = hiddenSeriesIdsRef.current
    list.forEach((item, index) => {
      const line = lineSeriesArr[index]
      if (!line || typeof line.setStrokeStyle !== 'function') return
      const color = item.color || colorForUnitIndex(index)
      try {
        if (hidden.has(item.unitUId)) {
          line.setStrokeStyle(transparentLine)
        } else {
          line.setStrokeStyle(buildStroke(color))
        }
      } catch (_) {
        /* ignore */
      }
    })
  }

  const toggleSeriesVisibility = (unitUId) => {
    onToggleSeriesVisibility?.(unitUId)
  }

  const fitAxesIfNeeded = (chart) => {
    if (!chart || autoFitDisabledRef.current) return
    try {
      arctionHelper.setAxisIntervals(chart)
      arctionRef.current?.saveInitialAxisIntervals?.()
    } catch (_) {
      /* ignore */
    }
  }

  useEffect(() => {
    if (!chartId || !containerRef.current || dateOriginMs == null) return undefined
    if (!document.getElementById(chartId)) return undefined

    const originDate = new Date(dateOriginMs)
    const arction = new ArctionDashboard({
      id: chartId,
      theme,
      height: CHART_HEIGHT,
      startTime: originDate,
    })

    let dashboard
    let chart
    try {
      dashboard = arction.createDashboard()
      chart = arction.createChart()
    } catch (e) {
      console.warn('[FleetChart] LCJS init failed, container may not be ready:', e)
      return undefined
    }
    chart.setPadding({ top: 8, left: 8, right: 12, bottom: 28 })
    const yAxis = arction.addYAxis({
      opposite: false,
      title: channelUnit ? `[${channelUnit}]` : '',
    })

    arctionRef.current = arction
    yAxisRef.current = yAxis

    const lineSeries = seriesList.map((item, index) => {
      const color = item.color || colorForUnitIndex(index)
      return chart
        .addLineSeries({
          dataPattern: { pattern: 'ProgressiveX' },
          yAxis,
        })
        .setStrokeStyle(buildStroke(color))
        .setName(item.unitName || item.unitUId || `Unit ${index + 1}`)
    })

    dashboardRef.current = dashboard
    chartRef.current = chart
    seriesRefsRef.current = lineSeries

    seriesList.forEach((item, i) => {
      if (!lineSeries[i] || !item.points?.length) return
      lineSeries[i].clear().add(toOriginOffsetPoints(item.points, dateOriginMs))
    })
    applySeriesVisibility(lineSeries, seriesList)

    const fmt = (value) => formatChartAxisDateTime(value)
    const axisX = chart.getDefaultAxisX()
    axisX.setTickStrategy(AxisTickStrategies.DateTime, (tickStrategy) => {
      let s = tickStrategy.setDateOrigin(originDate)
      if (s.setFormattingDay) s = s.setFormattingDay(fmt, fmt, fmt)
      if (s.setFormattingWeek) s = s.setFormattingWeek(fmt, fmt, fmt)
      if (s.setFormattingMonth) s = s.setFormattingMonth(fmt, fmt, fmt)
      if (s.setFormattingYear) s = s.setFormattingYear(fmt, fmt, fmt)
      s = s
        .setFormattingHour(fmt, fmt, fmt)
        .setFormattingMinute(fmt, fmt, fmt)
        .setFormattingSecond(fmt, fmt, fmt)
        .setCursorFormatter(fmt)
      return s
    })

    if (title) {
      chart.setTitle(`${title}${channelUnit ? ` (${channelUnit})` : ''}`)
      chart.setTitleFont((font) => font.setSize(13))
      // Same text colour as the rest of the app (LCJS theme default is near-black)
      chart.setTitleFillStyle(chartHelper.getFontColor(theme))
    }

    const layoutEngines = () => {
      try {
        dashboard.engine.layout()
        chart.engine.layout()
      } catch (_) {
        /* ignore */
      }
    }
    layoutEngines()
    const layoutRafId = requestAnimationFrame(layoutEngines)

    let resizeObserver
    if (typeof ResizeObserver !== 'undefined' && containerRef.current) {
      resizeObserver = new ResizeObserver(layoutEngines)
      resizeObserver.observe(containerRef.current)
    }

    if (hasData) {
      fitAxesIfNeeded(chart)
    }

    // Same interaction pattern as Jobs TimeBasedChart:
    // zoom/pan/wheel freezes autofit; double-click restores initial fit.
    const disableAutofit = () => {
      autoFitDisabledRef.current = true
      setAutoFitDisabled(true)
    }

    const onDragToken = chart.onSeriesBackgroundMouseDrag(() => {
      disableAutofit()
    })

    const yAxisDragToken = yAxis.onAxisInteractionAreaMouseDragStart(() => {
      disableAutofit()
    })

    const onWheelZoom = () => {
      disableAutofit()
    }
    chart.engine.container.addEventListener('wheel', onWheelZoom)

    const onDblClickToken = chart.onSeriesBackgroundMouseDoubleClick(() => {
      autoFitDisabledRef.current = false
      setAutoFitDisabled(false)
      try {
        if (typeof arction.resetChartToFullRange === 'function') {
          arction.resetChartToFullRange()
        } else {
          arctionHelper.setAxisIntervals(chart)
        }
        arction.saveInitialAxisIntervals?.()
      } catch (_) {
        try {
          arctionHelper.setAxisIntervals(chart)
        } catch (__) {
          /* ignore */
        }
      }
    })

    const mouseMoveToken = chart.onSeriesBackgroundMouseMove((_, event) => {
      try {
        const yAxisCurrent = yAxisRef.current
        if (!yAxisCurrent) return
        const mouseLocationEngine = chart.engine.clientLocation2Engine(event.clientX, event.clientY)
        const mouseLocationAxis = translatePoint(
          mouseLocationEngine,
          chart.engine.scale,
          { x: chart.getDefaultAxisX(), y: yAxisCurrent },
        )
        const origin = originRef.current || 0
        let absoluteMs = mouseLocationAxis.x
        if (absoluteMs < origin / 2) {
          absoluteMs = origin + mouseLocationAxis.x
        }
        cursorTouchedRef.current = true
        setCursorMs(absoluteMs)

        const currentSeries = seriesListRef.current || []
        const tolerance = getDateSpanTolerance(currentSeries)
        const nextValues = {}
        currentSeries.forEach((meta) => {
          nextValues[meta.unitUId] = valueAtTimestamp(meta.points, absoluteMs, tolerance)
        })
        setCursorValues(nextValues)
      } catch (_) {
        /* ignore */
      }
    })

    return () => {
      cancelAnimationFrame(layoutRafId)
      resizeObserver?.disconnect()
      try {
        chart.engine.container.removeEventListener('wheel', onWheelZoom)
      } catch (_) {
        /* ignore */
      }
      try {
        chart.offSeriesBackgroundMouseMove(mouseMoveToken)
        chart.offSeriesBackgroundMouseDrag(onDragToken)
        chart.offSeriesBackgroundMouseDoubleClick(onDblClickToken)
        yAxis.offAxisInteractionAreaMouseDragStart(yAxisDragToken)
      } catch (_) {
        /* ignore */
      }
      chart.dispose()
      dashboard.dispose()
      arctionRef.current = null
      chartRef.current = null
      dashboardRef.current = null
      seriesRefsRef.current = []
      yAxisRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartId, theme, dateOriginMs, seriesKey, title, channelUnit])

  // Append / refresh series data. Do NOT autofit while user has zoomed (Jobs pattern).
  useLayoutEffect(() => {
    const chart = chartRef.current
    const lineSeries = seriesRefsRef.current
    if (!chart || !lineSeries.length || dateOriginMs == null) return undefined

    seriesList.forEach((item, i) => {
      if (!lineSeries[i]) return
      if (!item.points?.length) {
        lineSeries[i].clear()
        return
      }
      lineSeries[i].clear().add(toOriginOffsetPoints(item.points, dateOriginMs))
    })
    applySeriesVisibility(lineSeries, seriesList)

    if (hasData && !autoFitDisabled) {
      fitAxesIfNeeded(chart)
    }

    const rafId = requestAnimationFrame(() => {
      const c = chartRef.current
      if (!c || !hasData || autoFitDisabledRef.current) return
      fitAxesIfNeeded(c)
    })
    return () => cancelAnimationFrame(rafId)
  }, [seriesList, dateOriginMs, hasData, autoFitDisabled])

  useEffect(() => {
    if (cursorTouchedRef.current) return
    const latest = {}
    let maxX = null
    seriesList.forEach((item) => {
      const finite = (item.points || []).filter((p) => Number.isFinite(p.y))
      latest[item.unitUId] = finite.length ? finite[finite.length - 1].y : null
      finite.forEach((p) => {
        if (maxX == null || p.x > maxX) maxX = p.x
      })
    })
    setCursorValues(latest)
    if (maxX != null) setCursorMs(maxX)
  }, [seriesList])

  const displayTime = cursorMs != null
    ? formatChartCursorTime(cursorMs)
    : '—'

  return (
    <div className={`fleetChartLayout${legendCollapsed ? ' is-legendCollapsed' : ''}`}>
      <div className="fleetChartMain">
        <div
          ref={containerRef}
          id={chartId}
          className="fleetChartCanvas"
          style={{ width: '100%', height: CHART_HEIGHT }}
        />
        {!hasData ? (
          <div className="fleetChartEmpty">Waiting for chart data…</div>
        ) : null}
      </div>

      <aside className="fleetChartLegend">
        <button
          type="button"
          className="fleetChartLegendToggle"
          onClick={() => setLegendCollapsed((v) => !v)}
          aria-label={legendCollapsed ? 'Expand legend' : 'Collapse legend'}
        >
          {legendCollapsed ? '‹' : '›'}
        </button>
        {!legendCollapsed ? (
          <>
            <div className="fleetChartLegendHead">
              <div className="fleetChartLegendTime">{displayTime}</div>
              <div className="fleetChartLegendUnit">{channelUnit || '—'}</div>
            </div>
            <ul className="fleetChartLegendList">
              {seriesList.map((item, index) => {
                const color = item.color || colorForUnitIndex(index)
                const value = cursorValues[item.unitUId]
                const isHidden = hiddenSeriesIds.has(item.unitUId)
                return (
                  <li
                    key={item.unitUId}
                    className={`fleetChartLegendRow${isHidden ? ' is-hidden' : ''}`}
                    onClick={() => toggleSeriesVisibility(item.unitUId)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        toggleSeriesVisibility(item.unitUId)
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    title={isHidden ? 'Show series' : 'Hide series'}
                  >
                    <span
                      className="fleetChartLegendSwatch"
                      style={{ backgroundColor: isHidden ? 'transparent' : color, borderColor: color }}
                    />
                    <span className="fleetChartLegendName" title={item.channelName || item.unitName}>
                      {item.unitName || item.unitUId}
                    </span>
                    <span className="fleetChartLegendValue">
                      {isHidden ? '—' : formatChartValue(value)}
                    </span>
                  </li>
                )
              })}
            </ul>
          </>
        ) : null}
      </aside>
    </div>
  )
}

export default FleetChart
